import React, { useState, useEffect, useRef } from 'react';
import * as pdfjsLib from 'pdfjs-dist';
import { AiOutlineClose, AiOutlineLeft, AiOutlineRight, AiOutlineZoomIn, AiOutlineZoomOut, AiOutlineDownload } from 'react-icons/ai';
import { useDocumentService } from '../services/api';

// Configurar PDF.js worker - usar worker local
pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js';

const PDFViewer = ({ documentId, onClose, operation = 'view' }) => {
  const documentService = useDocumentService();
  const [pdf, setPdf] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [scale, setScale] = useState(1.5);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const renderTaskRef = useRef(null);
  // Guard: evita doble fetch en dev por React.StrictMode (que corre useEffect 2 veces).
  // Sin este guard, cada apertura del visor registraba 2 eventos VISUALIZACION en
  // evento_documento. El ref se compara contra el documentId para que un cambio real
  // (abrir otro doc) sí dispare nuevo fetch.
  const lastLoadedDocIdRef = useRef(null);

  // Cargar PDF desde el endpoint de approval
  useEffect(() => {
    if (!documentId || lastLoadedDocIdRef.current === documentId) return;
    lastLoadedDocIdRef.current = documentId;

    const loadPDF = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await documentService.streamDocument(documentId, operation);

        const arrayBuffer = response.data;
        const pdfDoc = await pdfjsLib.getDocument(arrayBuffer).promise;

        setPdf(pdfDoc);
        setTotalPages(pdfDoc.numPages);
        setCurrentPage(1);
      } catch (err) {
        console.error('Error loading PDF:', err);
        setError('Error al cargar el documento. Por favor, inténtalo de nuevo.');
      } finally {
        setLoading(false);
      }
    };

    loadPDF();
  }, [documentId]); // Removido apiClient de las dependencias

  // Renderizar página actual
  useEffect(() => {
    const renderPage = async () => {
      if (!pdf || !canvasRef.current) return;

      try {
        // Cancelar operación de renderizado anterior si existe
        if (renderTaskRef.current) {
          renderTaskRef.current.cancel();
          renderTaskRef.current = null;
        }

        const page = await pdf.getPage(currentPage);
        const canvas = canvasRef.current;

        // Verificar que el canvas aún existe
        if (!canvas) return;

        const context = canvas.getContext('2d');

        const viewport = page.getViewport({ scale });
        canvas.height = viewport.height;
        canvas.width = viewport.width;

        // Limpiar el canvas después de cambiar las dimensiones
        context.clearRect(0, 0, canvas.width, canvas.height);

        const renderContext = {
          canvasContext: context,
          viewport: viewport
        };

        // Guardar referencia a la tarea de renderizado
        const renderTask = page.render(renderContext);
        renderTaskRef.current = renderTask;

        await renderTask.promise;
        renderTaskRef.current = null;
      } catch (err) {
        // Ignorar errores de cancelación
        if (err.name === 'RenderingCancelledException') {
          console.log('Renderizado cancelado correctamente');
          return;
        }
        console.error('Error rendering page:', err);
        setError('Error al renderizar la página del documento.');
      }
    };

    renderPage();
  }, [pdf, currentPage, scale]);

  // Cleanup al desmontar el componente
  useEffect(() => {
    return () => {
      if (renderTaskRef.current) {
        renderTaskRef.current.cancel();
        renderTaskRef.current = null;
      }
    };
  }, []);

  // Navegación
  const goToPreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const goToNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const zoomIn = () => {
    setScale(prev => Math.min(prev + 0.25, 3));
  };

  const zoomOut = () => {
    setScale(prev => Math.max(prev - 0.25, 0.5));
  };

  // Prevenir descarga y otras acciones
  useEffect(() => {
    const preventDownload = (e) => {
      // Prevenir Ctrl+S, Ctrl+A, Ctrl+C, Ctrl+P
      if ((e.ctrlKey || e.metaKey) && (e.key === 's' || e.key === 'a' || e.key === 'c' || e.key === 'p')) {
        e.preventDefault();
        e.stopPropagation();
        return false;
      }

      // Prevenir F12 (DevTools)
      if (e.key === 'F12') {
        e.preventDefault();
        return false;
      }
    };

    const preventRightClick = (e) => {
      e.preventDefault();
      return false;
    };

    const preventSelection = (e) => {
      e.preventDefault();
      return false;
    };

    // Agregar event listeners
    document.addEventListener('keydown', preventDownload);
    document.addEventListener('contextmenu', preventRightClick);
    document.addEventListener('selectstart', preventSelection);
    document.addEventListener('dragstart', preventSelection);

    // Cleanup
    return () => {
      document.removeEventListener('keydown', preventDownload);
      document.removeEventListener('contextmenu', preventRightClick);
      document.removeEventListener('selectstart', preventSelection);
      document.removeEventListener('dragstart', preventSelection);
    };
  }, []);

  if (loading) {
    return (
      <div style={styles.overlay}>
        <div style={styles.modal}>
          <div style={styles.loadingContainer}>
            <div style={styles.spinner}></div>
            <p style={styles.loadingText}>Cargando documento...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.overlay}>
        <div style={styles.modal}>
          <div style={styles.errorContainer}>
            <h3 style={styles.errorTitle}>Error al cargar el documento</h3>
            <p style={styles.errorText}>{error}</p>
            <button style={styles.retryButton} onClick={() => window.location.reload()}>
              Reintentar
            </button>
            <button style={styles.closeButton} onClick={onClose}>
              Cerrar
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.overlay}>
      <div style={styles.modal}>
        {/* Header con controles */}
        <div style={styles.header}>
          <div style={styles.headerLeft}>
            <h3 style={styles.title}>Vista Previa del Documento</h3>
            <span style={styles.pageInfo}>
              Página {currentPage} de {totalPages}
            </span>
          </div>

          <div style={styles.headerRight}>
            <div style={styles.controls}>
              <button
                style={styles.controlButton}
                onClick={zoomOut}
                title="Alejar"
              >
                <AiOutlineZoomOut />
              </button>
              <span style={styles.zoomText}>{Math.round(scale * 100)}%</span>
              <button
                style={styles.controlButton}
                onClick={zoomIn}
                title="Acercar"
              >
                <AiOutlineZoomIn />
              </button>
            </div>

            <button style={styles.closeButton} onClick={onClose} title="Cerrar">
              <AiOutlineClose />
            </button>
          </div>
        </div>

        {/* Contenido del PDF */}
        <div style={styles.content} ref={containerRef}>
          <div style={styles.pdfContainer}>
            <canvas
              ref={canvasRef}
              style={styles.canvas}
              onContextMenu={(e) => e.preventDefault()}
              onDragStart={(e) => e.preventDefault()}
            />
          </div>
        </div>

        {/* Footer con navegación */}
        <div style={styles.footer}>
          <div style={styles.navigation}>
            <button
              style={styles.navButton}
              onClick={goToPreviousPage}
              disabled={currentPage <= 1}
              title="Página anterior"
            >
              <AiOutlineLeft />
            </button>

            <span style={styles.pageIndicator}>
              {currentPage} / {totalPages}
            </span>

            <button
              style={styles.navButton}
              onClick={goToNextPage}
              disabled={currentPage >= totalPages}
              title="Página siguiente"
            >
              <AiOutlineRight />
            </button>
          </div>

          <div style={styles.watermarkNotice}>
            <span style={styles.watermarkText}>
              🔒 Documento con restricciones de seguridad
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

const styles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.8)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  modal: {
    backgroundColor: '#FFFFFF',
    borderRadius: '12px',
    width: '95%',
    height: '90%',
    maxWidth: '1200px',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px 20px',
    backgroundColor: '#F8F9FA',
    borderBottom: '1px solid #E5E7EB',
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
  },
  title: {
    margin: 0,
    fontSize: '18px',
    fontWeight: '600',
    color: '#111827',
  },
  pageInfo: {
    fontSize: '14px',
    color: '#6B7280',
  },
  headerRight: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  controls: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  controlButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '32px',
    height: '32px',
    backgroundColor: '#F3F4F6',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    color: '#374151',
    fontSize: '16px',
  },
  zoomText: {
    fontSize: '12px',
    color: '#6B7280',
    minWidth: '40px',
    textAlign: 'center',
  },
  closeButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '32px',
    height: '32px',
    backgroundColor: '#DC2626',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    color: '#FFFFFF',
    fontSize: '16px',
  },
  content: {
    flex: 1,
    overflow: 'auto',
    backgroundColor: '#F9FAFB',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'flex-start',
    padding: '20px',
  },
  pdfContainer: {
    backgroundColor: '#FFFFFF',
    borderRadius: '8px',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
    overflow: 'hidden',
  },
  canvas: {
    display: 'block',
    maxWidth: '100%',
    height: 'auto',
    userSelect: 'none',
    pointerEvents: 'none',
  },
  footer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px 20px',
    backgroundColor: '#F8F9FA',
    borderTop: '1px solid #E5E7EB',
  },
  navigation: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  navButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '36px',
    height: '36px',
    backgroundColor: '#3B82F6',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    color: '#FFFFFF',
    fontSize: '16px',
  },
  pageIndicator: {
    fontSize: '14px',
    color: '#374151',
    minWidth: '80px',
    textAlign: 'center',
  },
  watermarkNotice: {
    display: 'flex',
    alignItems: 'center',
  },
  watermarkText: {
    fontSize: '12px',
    color: '#6B7280',
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '300px',
    gap: '16px',
  },
  spinner: {
    width: '40px',
    height: '40px',
    border: '4px solid #F3F4F6',
    borderTop: '4px solid #3B82F6',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
  loadingText: {
    fontSize: '16px',
    color: '#6B7280',
    margin: 0,
  },
  errorContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '300px',
    gap: '16px',
    textAlign: 'center',
  },
  errorTitle: {
    fontSize: '18px',
    color: '#DC2626',
    margin: 0,
  },
  errorText: {
    fontSize: '14px',
    color: '#6B7280',
    margin: 0,
  },
  retryButton: {
    padding: '8px 16px',
    backgroundColor: '#3B82F6',
    color: '#FFFFFF',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '500',
  },
};

// Agregar animación CSS para el spinner
const style = document.createElement('style');
style.textContent = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;
document.head.appendChild(style);

export default PDFViewer;
